import React from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Link as MUILink,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

/** Body copy inside a legal section. */
export const P = ({ children }) => (
  <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8, '&:last-child': { mb: 0 } }}>
    {children}
  </Typography>
);

/** Bulleted list inside a legal section. */
export const Bullets = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, m: 0, mb: 2, '& li': { mb: 1.25 } }}>
    {items.map((item, i) => (
      <Typography component="li" key={i} variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
        {item}
      </Typography>
    ))}
  </Box>
);

/** Sub-heading within a legal section. */
export const SubHeading = ({ children }) => (
  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, color: 'text.primary', mt: 3, mb: 1.5 }}>
    {children}
  </Typography>
);

/**
 * Shared chrome for the Privacy and Terms pages: aurora backdrop, page heading,
 * contents index and the contact block. Both pages supply `sections` so their
 * headings, anchors and index can never drift out of sync.
 */
const LegalLayout = ({ eyebrow, title, intro, lastUpdated, sections }) => {
  const theme = useTheme();

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', backgroundColor: 'background.default' }}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: theme.surfaces.aurora,
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', py: { xs: 5, md: 9 } }}>
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBack />}
          sx={{ mb: 4, fontWeight: 700, px: 2, ml: -2 }}
        >
          Back to Home
        </Button>

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', display: 'block', mb: 1.5, fontSize: '0.75rem' }}
          >
            {eyebrow}
          </Typography>
          <Typography variant="h2" component="h1" sx={{ fontWeight: 800, mb: 2 }}>
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 640, lineHeight: 1.8 }}>
            {intro}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5 }}>
            Last updated:{' '}
            <Box component="time" dateTime={lastUpdated} sx={{ fontWeight: 600 }}>
              {new Date(lastUpdated).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            </Box>
          </Typography>
        </Box>

        <Paper
          elevation={0}
          component="nav"
          aria-label="On this page"
          sx={{
            p: { xs: 2.5, md: 3 },
            mb: 4,
            borderRadius: 4,
            ...theme.surfaces.glass,
            borderColor: alpha(theme.palette.primary.main, 0.16)
          }}
        >
          <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 800, mb: 1.5 }}>
            On this page
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
            }}
          >
            {sections.map((section, i) => (
              <Box component="li" key={section.id} sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
                <Box component="span" aria-hidden sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.85rem' }}>
                  {String(i + 1).padStart(2, '0')}
                </Box>
                <MUILink href={`#${section.id}`} color="text.secondary" variant="body2" sx={{ fontWeight: 600 }}>
                  {section.title}
                </MUILink>
              </Box>
            ))}
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            backgroundColor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            boxShadow: `0 18px 48px ${alpha(theme.palette.primary.main, 0.06)}`
          }}
        >
          <Stack spacing={5}>
            {sections.map((section, i) => (
              <Box
                key={section.id}
                component="section"
                id={section.id}
                sx={{ scrollMarginTop: 24 }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'baseline', gap: 1.5 }}
                >
                  <Box component="span" aria-hidden sx={{ color: 'primary.main', fontSize: '0.85rem', fontWeight: 700 }}>
                    {String(i + 1).padStart(2, '0')}
                  </Box>
                  {section.title}
                </Typography>
                <Box
                  sx={{
                    color: 'text.secondary',
                    '& p': { mb: 2, lineHeight: 1.8 },
                    '& p:last-child': { mb: 0 }
                  }}
                >
                  {section.content}
                </Box>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 5 }} />

          <Box
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
            }}
          >
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Software Systems International (Pvt) Ltd</Typography>
            {/*
              This block sits on a tinted surface, where text.secondary (4.32:1) and
              primary.main (4.32:1) fall short of AA. text.primary and primary.dark
              clear it comfortably and are already part of the palette.
            */}
            <Stack spacing={0.75}>
              <Typography variant="body2" color="text.primary">
                Email:{' '}
                <MUILink href="mailto:info@ssipl.lk" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                  info@ssipl.lk
                </MUILink>
              </Typography>
              <Typography variant="body2" color="text.primary">
                Phone:{' '}
                <MUILink href="tel:+94716474842" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                  +94 (0) 71 647 4842
                </MUILink>
              </Typography>
              <Typography variant="body2" color="text.primary">
                Address: Colombo, Sri Lanka
              </Typography>
            </Stack>
          </Box>
        </Paper>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mt: 4 }}
        >
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Software Systems International (Pvt) Ltd.
          </Typography>
          <Stack direction="row" spacing={2}>
            <MUILink component={RouterLink} to="/privacy" color="text.secondary" variant="body2" sx={{ fontWeight: 600 }}>
              Privacy
            </MUILink>
            <MUILink component={RouterLink} to="/terms" color="text.secondary" variant="body2" sx={{ fontWeight: 600 }}>
              Terms
            </MUILink>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default LegalLayout;
